import type { Dispatch, SetStateAction } from 'react'

import type {
  FanNViewDto,
  FanResponseDto,
  FanXViewDto,
  OnlyFansChatDto,
  OnlyFansList,
  OnlyFansMessage,
  VaultMediaFilesDto,
  VaultMediaItemDto,
  VaultMediaType,
} from '@api'
import { type CancelToken } from 'axios'

import type { Creator } from '../creators/types'

export interface ChattingContextValue {
  onlineCount: OnlineCountConfig | null
  highlightedNumber: number
  setHighlightedNumber: Dispatch<SetStateAction<number>>
  replyToMessage?: Message
  setReplyToMessage: Dispatch<SetStateAction<Message | undefined>>
  isScriptAutoSuggestionEnabled: boolean
  setIsScriptAutoSuggestionEnabled: Dispatch<SetStateAction<boolean>>
}

export interface ChatItem {
  id: number
  lastMessageId?: number
  receiveDate: number
  order: number
}

export type Chat = Omit<OnlyFansChatDto, 'lastMessage'> & {
  lastMessageId?: number
}

export interface MessageItem {
  id: number
  createdAt: number
}

export type Message = OnlyFansMessage & {
  // This field is not present in the API response
  sentDate: string
  _isTempMessage?: boolean
  _hasError?: boolean
}

export interface MessageSendRequest {
  lockedText: boolean
  mediaFiles: (UploadedFileDetails | number)[]
  previews: (number | string)[]
  price: number
  rfGuest?: number[]
  rfPartner?: number[]
  rfTag?: number[]
  text: string
  replyToMessageId: any
  isScheduled: number
  scheduledDate: string
  excludedLists?: OnlyFansList['id'][]
  userLists?: OnlyFansList['id'][]
  userIds?: FanResponseDto['id'][]
  excludeUserIds?: FanResponseDto['id'][]
  giphyId?: string | null
  scriptId?: number
  priorityMassMessage?: boolean
  mediaCount?: number
}

export type Media = Pick<
  VaultMediaItemDto,
  'id' | 'type' | 'duration' | 'files' | 'hasError'
>

export interface MassMessage {
  id: number
  userId: number
  originalUserId: number
  date: string
  text: string
  giphyId: string | null
  textCropped: string
  isFree: boolean
  sentCount: number
  viewedCount: number
  price?: string
  canUnsend: boolean
  unsendSeconds: number
  isCanceled: boolean
  mediaTypes: { [key: string]: number } | null
  hasError: boolean
  purchasedCount?: number
  canSendMessageToBuyers?: boolean
  releaseForms: any[]
}

export interface UpcomingBirthday {
  birthday: string
  name: string
  onlyfansId: number
}

export interface ListToUpdate {
  listId: string
  pinned: boolean
}

export type TinyChatsList = Pick<OnlyFansList, 'id' | 'name'>
export type ListSortOptions = 'name' | 'people' | 'recent' | 'custom'
export type ListOrderOptions = 'asc' | 'desc'

export interface GetListsResponse {
  hasMore: boolean
  list: OnlyFansList[]
  order: ListOrderOptions
  sort: ListSortOptions
}

export interface ClearChatRequestOptions {
  creatorId: number
  userId: number
}
export interface CommonChatActionsParams {
  creatorId: number
  userId: number
}

export interface CommonChatModificationParams {
  creatorId: number
  fanId: number
}

export interface GetMassMessagesStatisticsRequestOptions {
  page: number
  query?: string
  creatorId: number
  endDate: string | null
}

export interface GetMassMessagesStatisticsResponse {
  list: MassMessage[]
  hasMore: boolean
}

export type MuteChatParams = CommonChatModificationParams & {
  unmuteAt: string
}

export type SendMessageParams = CommonChatActionsParams & {
  message: Partial<MessageSendRequest>
  chat?: OnlyFansChatDto
}

export type SetDisplayNameParams = CommonChatActionsParams & {
  name: string
}

export type MessagesStackDirection = 'up' | 'down'

export interface GetMessagesParams {
  creatorId: number
  fanId: number
  limit: number
  anchorMessageId?: number
  direction: MessagesStackDirection
}

export interface GetMessagesResponse {
  hasMore: boolean
  list: Message[]
  direction: MessagesStackDirection
  fanId: number
  anchorMessageId: number
}

export interface FanCountdown {
  end: number
  chatName: string
}

export interface Audio {
  id: string
  name: string
  description?: string
  contentType: string
  duration: number
}

export interface EditAudioRequestOptions {
  id: string
  name: string
  description?: string
  creatorId: number
}

export interface UploadFileRequestOptions {
  file: File
  name: string
  description?: string
}

export interface GetUpcomingBirthdaysResponse {
  days: Record<string, UpcomingBirthday[]>
}

export interface UploadMediaRequestOptions {
  file: File
  fileName: string
  creatorId: number
  selectedCategoryId?: number
  onUploadProgress?: (params: { total: number; loaded: number }) => void
  cancelToken?: CancelToken
  isVaultMedia?: boolean
}

export interface UploadedFileDetails {
  processId: string
  host: string
  name: string
  thumbs?: { id: number; url: string }[]
  isPreview?: boolean
}

// It can be a number as ID, if the media already existed
export type UploadMediaResponse = number | UploadedFileDetails

export interface MultipartStartResponse {
  getUrl: string
  uploadId: string
  storageKey: string
  keys: Array<{
    putUrl: string
    part: number
  }>
}

export interface MultipartFinishRequest {
  storageKey: string
  getUrl: string
  uploadId: string
  contentLength: number
  fileName: string
  contentType: string
  skipDuplicateCheck: boolean
  forcedProcessing: boolean
  includeWatermark: boolean
  parts: Array<{
    PartNumber: number
    ETag: string
  }>
}

export interface MediaAttachment {
  blob: Blob
  media: UploadMediaResponse
  duration: number
  url: string
}

export interface ChattingTab {
  fanId: FanResponseDto['id']
  isActive: boolean
  title: string
  avatar?: FanResponseDto['avatar']
  fanNumber?: number | null
}

export interface EditPinnedListStateRequestOptions {
  creatorId: number
  listsToUpdate: ListToUpdate[]
}

export interface EditBirthdayRequestOptions {
  creatorId: number
  userId: number
  birthday: string
}

export interface GetUsersListFilters {
  total_spent?: number
  tips?: number
  duration?: number
  inactive?: number
  online?: boolean
  recommenders?: boolean
  signed_up_before?: string
  signed_up_after?: string
}

export type NotificationTypeBase =
  | 'all'
  | 'likes'
  | 'subscribed'
  | 'tip'
  | 'commented'
  | 'mentioned'
  | 'favorited'
  | 'message'
  | 'system'
  | 'tags'
  | 'deactivated_media'

export type UnreadNotificationType =
  | 'all'
  | 'commented'
  | 'deactivated_media'
  | 'favorited'
  | 'mentioned'
  | 'message'
  | 'purchases'
  | 'subscribed'
  | 'system'
  | 'tags'
  | 'tip'

export type UnreadNotificationsCountResponse = Record<
  UnreadNotificationType,
  number
>

export type NotificationTypeBody = NotificationTypeBase | 'paided_message'
export type NotificationTypeRequest = NotificationTypeBase | 'purchases'

export interface Notification {
  id: number
  type: NotificationTypeBody
  createdAt: string
  text: string
  subType: string
  browserName: string
  canGoToProfile: boolean
  user: FanNViewDto
  originallyAssignedUserId: number
  assignedUserId: number
  referencingMessageId: number
  read: boolean
  replacePairs: Record<string, string>
}

export interface GetNotificationsOptions {
  creatorId: number
  type: NotificationTypeRequest
  anchorId: Notification['id']
}

export interface GetNotificationsResponse {
  list: Notification[]
  hasMore: boolean
}

export interface SentMediaMessageResponse {
  sentMediaMessages: SentMediaMessage[]
  postedMediaIds: number[]
}

export interface SentMediaMessage {
  fanId: number
  id: number
  createdAt: string
  isOpened: boolean
  purchaseDate: string | null
  price: number
  text: string
  isFree: boolean
  canPurchaseReason?: string
  first: boolean
  sentBy: 'fan' | 'creator'
  media: SentMediaMessageMedia[]
}

export interface SentMediaMessageMedia {
  id: number
  type: string
  /**
   * Virtual prop, mapped on client.
   */
  files?: VaultMediaFilesDto // TODO: Not implemented yet for PPV Timeline yet
  duration: number
}

export interface ListCreationProgress {
  total: number
  success: number
  failure: number
  remaining: number
  completed: boolean
}

export interface OnlineCountConfig {
  count: number
  updatedAt: number
}

export enum ChatMediaType {
  All = 'all', // not an actual vault media type. used on filters
  Unlocked = 'unlocked', // not an actual vault media type. used on filters
  Video = 'video',
  Photo = 'photo',
  Audio = 'audio',
}

export type ListUser = FanXViewDto & {
  isPinned?: boolean // This field is not present in the API response
  values: any
}

export interface GetListUsersOptions {
  creatorId: Creator['id']
  listId: OnlyFansList['id']
  pinned: boolean
  offset: number
  order: string
  sort: ListUsersDirectionOptions
  query: string
}

export interface GetListUsersResponse {
  list: ListUser[]
  hasMore: boolean
  count: number
  nextOffset: number | null
  pinned: boolean
}

export interface SpendingHistory {
  expireDate: string
  price: number
  subscribeDate: string
}

export interface GetSpendingHistoryResponse {
  list: SpendingHistory[]
  hasMore: boolean
}

export type ListUsersOrderOptions = 'default' | 'recent'
export type FollowingListUsersOrderOptions =
  | 'last_activity'
  | 'expire_date'
  | 'subscribe_date'
export type ListUsersDirectionOptions = 'asc' | 'desc'

export interface GetListUsersOptionsBase {
  creatorId: Creator['id']
  offset: number
  query: string
}

export type GetSubscribersListOptions = GetListUsersOptionsBase & {
  type: 'all' | 'active' | 'expired' | 'activity'
  filters: GetUsersListFilters
}

export type GetRestrictedUsersOptions = GetListUsersOptionsBase

export type GetBlockedUsersOptions = GetListUsersOptionsBase & {
  order: string
  sort: ListUsersDirectionOptions
}

export type GetFollowingUsersOptions = GetListUsersOptionsBase & {
  order: string
  sort: ListUsersDirectionOptions
  type: 'all' | 'active' | 'expired'
  filters: FollowingUsersFilters
}

export interface GetListsUsersCountResponse {
  bookmarks: number
  subscribers: {
    active: number
    activeOnline: number
    all: number
    blocked: number
    expired: number
    muted: number
    restricted: number
  }
  subscriptions: {
    active: number
    all: number
    attention: number
    blocked: number
    expired: number
    muted: number
    restricted: number
  }
}

export interface FollowingUsersFilters {
  paid: 0 | 1 | null
  online: 0 | 1 | null
}

export interface MessageQueue {
  id: number
  date: string
  isReady: boolean
  isDone: boolean
  total: number // -1 means hasn't started yet
  pending: number
  canUnsend: boolean
  unsendSeconds: number
  hasError: boolean
  isCanceled: boolean
  isCouplePeopleMedia: boolean
}

export interface CloneMediaJobResponse {
  id: string
  contentType: string
  mediaType: VaultMediaType
  progress: number
  status: string
  done: boolean
  media: null | VaultMediaItemDto
}

export interface UpdateInList {
  listId: number
  add: boolean
}

export interface UpdateListsUserRequestOptions {
  data: UpdateInList[]
}

export interface PpvStatistics {
  averagePrice: number
  maximumPrice: number
  totalCount: number
  boughtCount: number
  lastPurchase: string | null
}

export interface FanOriginCategory {
  id: number
  name: string
}

export type TaggableItemsSortOptions = 'date' | 'name'
export type TaggableItemsFilterOptions = 'all' | 'pending'
